import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.esm.js';
import type { AppConfig } from './config';
import type { Session } from './auth';
import {
  addReply,
  createComment,
  getPeaks,
  getPlaybackUrl,
  listComments,
  listReplies,
  listTrackVersions,
  restoreVersion,
  setCommentResolved,
  type Comment,
  type Reply,
  type VersionSummary,
} from './api';
import { matchGainDb } from './loudness';
import { Btn, Icon, t, ShortcutsHelp, type ShortcutRow } from './ui';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

// Unified track player (Phase 3 UX rework).
//
// ONE transport for the whole track. Two decoupled halves that never share a media element:
//
//  1. AUDIO — a Web Audio graph of up to three position-synced <audio> sources (slots A/B/C),
//     each `createMediaElementSource -> GainNode -> destination`. All play together; only the
//     active slot's gain is up. Switching flips the gain + copies currentTime, so it is gapless
//     and playback NEVER stops (the playhead keeps moving, you just hear the other version).
//
//  2. WAVEFORM — WaveSurfer, VISUAL ONLY, on its OWN muted element. It renders from PRECOMPUTED
//     peaks (the ingest worker's peaks JSON, fetched via /peaks). WaveSurfer's default MediaElement
//     backend never decodes audio itself, so supplying peaks is the ONLY way the waveform renders
//     — and it means switching versions redraws instantly without any audio fetch/decode, so it
//     cannot disturb playback. The cursor is driven from the active element's currentTime; clicks
//     on the waveform seek the graph.
//
// Comments always target the CURRENTLY-SELECTED (active) version. Posting defaults to
// "pin to this version only" checked.
//
// Loudness match (Req 4.4, 4.5): when ON, each audible source's gain = matchGainDb(...) to hit the
// project reference, clamped so post-gain true peak stays <= -1 dBTP. When OFF, unity.

// Waveform overlay colours — tuned for the DARK canvas (brighter/more opaque than a light theme
// would need so regions/pins stay visible on black).
const REGION_COLOR = 'rgba(77, 184, 184, 0.28)';   // comment region fill (accent teal)
const REGION_EDGE = 'rgba(77, 184, 184, 0.95)';    // solid tag edge on a region comment's start
const POINT_COLOR = 'rgba(232, 92, 78, 0.95)';     // point-comment pin (warm red)
const LOOP_COLOR = 'rgba(150, 135, 255, 0.45)';    // loop span — set apart from comments
const SLOTS = ['A', 'B', 'C'] as const;
type Slot = (typeof SLOTS)[number];

const dbToGain = (db: number) => Math.pow(10, db / 20);

function fmt(ms: number): string {
  const s = Math.floor(ms / 1000);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  const cs = Math.floor((ms % 1000) / 10);
  return `${mm}:${ss.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}

function renderBody(body: string) {
  const parts = body.split(/(@[A-Za-z0-9][A-Za-z0-9_.-]{0,63})/g);
  return parts.map((p, i) =>
    /^@[A-Za-z0-9]/.test(p)
      ? <strong key={i} style={{ color: 'var(--accent)' }}>{p}</strong>
      : <span key={i}>{p}</span>
  );
}

type Filter = 'all' | 'open' | 'resolved' | 'mine';

interface Loaded {
  el: HTMLAudioElement;
  src: MediaElementAudioSourceNode;
  gain: GainNode;
  version: VersionSummary;
  peaks: number[];        // normalized -1..1, single channel (for the waveform library)
  durationSecs: number;   // supplied to the waveform library so it renders without fetching audio
}

export function TrackPlayer({
  config,
  session,
  projectId,
  trackId,
  loudnessRef = -14,
  canComment,
  onApprovalContextChange,
}: {
  config: AppConfig;
  session: Session;
  projectId: string;
  trackId: string;
  loudnessRef?: number;
  canComment: boolean;
  /** Notifies the parent which version is currently selected (for the approval/export panel). */
  onApprovalContextChange?: (versionId: string | null) => void;
}) {
  // --- Version selection (A/B/C slots) ---
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [slotIds, setSlotIds] = useState<Record<Slot, string | null>>({ A: null, B: null, C: null });
  const [active, setActive] = useState<Slot>('A');
  const [playing, setPlaying] = useState(false);
  const [loudnessMatch, setLoudnessMatch] = useState(false);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playheadMs, setPlayheadMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Loop / repeat (session-only). Bounds in ms; enforced in the rAF tick.
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopStartMs, setLoopStartMs] = useState<number | null>(null);
  const [loopEndMs, setLoopEndMs] = useState<number | null>(null);
  // Mirror for the rAF loop so it reads current values without re-subscribing each change.
  const loopRef = useRef<{ enabled: boolean; startMs: number | null; endMs: number | null }>({
    enabled: false, startMs: null, endMs: null,
  });

  // --- Comment state (for the ACTIVE version) ---
  const [comments, setComments] = useState<Comment[]>([]);
  const [draft, setDraft] = useState('');
  const [pinToVersion, setPinToVersion] = useState(true); // tweak #1: default checked
  const [pendingRegion, setPendingRegion] = useState<{ startMs: number; endMs: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [threads, setThreads] = useState<Record<string, Reply[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});
  const [showHelp, setShowHelp] = useState(false);

  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const waveRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<RegionsPlugin | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  // Audio sources keyed by versionId (a version selected in two slots is loaded once).
  const loadedRef = useRef<Map<string, Loaded>>(new Map());
  // Mirror of `playing` for use inside the graph-build effect without adding it to the deps
  // (which would rebuild the whole graph on every play/pause).
  const playingRef = useRef(false);

  const activeVersionId = slotIds[active];

  // --- Load the track's ready versions; default A=newest, B=next. ---
  useEffect(() => {
    listTrackVersions(config, session, projectId, trackId)
      .then((res) => {
        const r = res.versions.filter((v) => v.primaryAsset?.ingestStatus === 'ready');
        setVersions(r);
        setSlotIds({
          A: r[0]?.versionId ?? null,
          B: r[1]?.versionId ?? null,
          C: null,
        });
      })
      .catch((e) => setError(`Could not load versions: ${String(e)}`));
  }, [config, session, projectId, trackId]);

  const versionById = useMemo(() => {
    const m = new Map<string, VersionSummary>();
    versions.forEach((v) => m.set(v.versionId, v));
    return m;
  }, [versions]);

  // Distinct versionIds currently selected across slots (what the audio graph must hold).
  const selectedIds = useMemo(
    () => [...new Set(SLOTS.map((s) => slotIds[s]).filter((id): id is string => !!id))],
    [slotIds]
  );

  const audibleGainFor = useCallback(
    (v: VersionSummary | undefined): number => {
      if (!v?.primaryAsset) return 1;
      if (!loudnessMatch) return 1;
      return dbToGain(matchGainDb(v.primaryAsset.integratedLufs, v.primaryAsset.truePeakDbtp, loudnessRef));
    },
    [loudnessMatch, loudnessRef]
  );

  const teardownAudio = useCallback(() => {
    for (const l of loadedRef.current.values()) {
      l.el.pause();
      try { l.src.disconnect(); l.gain.disconnect(); } catch { /* already gone */ }
      l.el.src = '';
    }
    loadedRef.current = new Map();
  }, []);

  // --- Build the WaveSurfer WAVEFORM once. ---
  // Important: WaveSurfer is used ONLY as a visual (waveform + cursor + click-to-seek + region
  // markers). It is NOT the audio transport — the Web Audio graph below is. WaveSurfer owns its
  // OWN muted <audio> element (so it never fights the graph and never doubles the sound); we load
  // a version's proxy URL into it purely to render peaks, drive the cursor from the graph's
  // timeupdate, and translate clicks into seeks on the graph.
  useEffect(() => {
    // Wait until the container is mounted AND visible (versions loaded → not display:none), and
    // only create once. Creating against a hidden/0-size container renders an empty waveform.
    if (wsRef.current) return;              // already created
    if (!waveRef.current) return;           // container not mounted yet
    if (versions.length === 0) return;      // container still hidden (display:none)
    const regions = RegionsPlugin.create();
    const ws = WaveSurfer.create({
      container: waveRef.current,
      height: 110,
      waveColor: '#4a6a6a',
      progressColor: '#4db8b8',
      // Bold, high-contrast playhead — light so it stands out on the dark waveform canvas.
      cursorColor: '#ffffff',
      cursorWidth: 3,
      interact: true,
      // Bars make WaveSurfer use renderBarWaveform (which reads one magnitude per bucket — the
      // shape our precomputed peaks are in) and gives the usual mixer-style waveform look.
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      plugins: [regions],
    });
    wsRef.current = ws;
    regionsRef.current = regions;
    // Never let WaveSurfer's own element make sound; the graph is the only audible path.
    ws.setVolume(0);
    ws.on('ready', () => ws.setVolume(0));

    // A click/drag on the waveform seeks: WaveSurfer reports the target time; we move the graph.
    ws.on('interaction', (t: number) => seekGraph(t));

    regions.enableDragSelection({ color: REGION_COLOR });
    regions.on('region-created', (r: Region) => {
      if (r.id.startsWith('comment-')) return;
      regions.getRegions().forEach((ex) => {
        if (!ex.id.startsWith('comment-') && ex.id !== r.id) ex.remove();
      });
      // Mark the transient selection distinctly (dashed edges) so an in-progress region reads
      // differently from a committed region comment (solid tag edge) or the loop span.
      const el = (r as unknown as { element?: HTMLElement }).element;
      if (el) {
        el.style.border = `1px dashed ${REGION_EDGE}`;
        el.style.boxSizing = 'border-box';
      }
      setPendingRegion({ startMs: Math.floor(r.start * 1000), endMs: Math.floor(r.end * 1000) });
    });
    regions.on('region-clicked', (r: Region) => {
      if (r.id.startsWith('comment-')) seekGraph(r.start);
    });
    // NOTE: no cleanup here — this effect may re-run when versions.length changes, and we must
    // NOT destroy the (single) WaveSurfer instance on those re-runs. Teardown is a separate
    // unmount-only effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.length]);

  // Destroy WaveSurfer only on real unmount.
  useEffect(() => () => {
    wsRef.current?.destroy();
    wsRef.current = null;
    regionsRef.current = null;
  }, []);

  // --- Build / rebuild the audio graph whenever the set of selected versions changes. ---
  useEffect(() => {
    if (selectedIds.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const build = async () => {
      const ctx = ctxRef.current ?? new AudioContext();
      ctxRef.current = ctx;

      // Remove sources no longer selected.
      for (const [vid, l] of [...loadedRef.current.entries()]) {
        if (!selectedIds.includes(vid)) {
          l.el.pause();
          try { l.src.disconnect(); l.gain.disconnect(); } catch { /* gone */ }
          l.el.src = '';
          loadedRef.current.delete(vid);
        }
      }

      // Add newly-selected sources.
      const toAdd = selectedIds.filter((vid) => !loadedRef.current.has(vid));
      try {
        await Promise.all(
          toAdd.map(async (vid) => {
            const version = versionById.get(vid)!;
            const asset = version.primaryAsset!;
            // Fetch the streaming proxy URL (for audio) and the precomputed peaks (for the
            // waveform) together. Peaks make the waveform render instantly with no audio decode.
            const [{ url }, peaksRes] = await Promise.all([
              getPlaybackUrl(config, session, vid, asset.assetId),
              getPeaks(config, session, vid, asset.assetId),
            ]);
            const el = new Audio();
            el.crossOrigin = 'anonymous';
            el.preload = 'auto';
            el.src = url;
            const src = ctx.createMediaElementSource(el);
            const gain = ctx.createGain();
            gain.gain.value = 0;
            src.connect(gain).connect(ctx.destination);
            await new Promise<void>((resolve, reject) => {
              el.addEventListener('loadedmetadata', () => resolve(), { once: true });
              el.addEventListener('error', () => reject(new Error(`load failed for ${vid}`)), { once: true });
            });
            if (cancelled) { el.pause(); return; }
            // The ingest worker stores INTERLEAVED int16 min/max pairs: [min0,max0,min1,max1,...].
            // WaveSurfer, however, reads channelData[0] as one magnitude PER BUCKET (it takes
            // Math.abs of each element and maps array length across the width). So collapse each
            // (min,max) pair into a single normalized magnitude = max(|min|,|max|)/32768, giving
            // exactly `buckets` values that map 1:1 across the waveform.
            const raw = Array.isArray(peaksRes.peaks?.data) ? peaksRes.peaks.data : [];
            const peaks: number[] = [];
            for (let i = 0; i + 1 < raw.length; i += 2) {
              peaks.push(Math.max(Math.abs(raw[i]), Math.abs(raw[i + 1])) / 32768);
            }
            const durationSecs =
              (peaksRes.durationMs ?? asset.durationMs ?? 0) / 1000 || el.duration || 0;
            loadedRef.current.set(vid, { el, src, gain, version, peaks, durationSecs });
          })
        );
        if (cancelled) return;
        // If playback is already going, bring any newly-added sources (e.g. a version just picked
        // for slot C) into lockstep: seek them to the current position and start them (muted via
        // their gain). Otherwise they'd sit paused at 0 and switching to them would be silent.
        if (playingRef.current && toAdd.length) {
          const anyActive = activeVersionId ? loadedRef.current.get(activeVersionId)?.el : undefined;
          const pos = anyActive?.currentTime ?? 0;
          for (const vid of toAdd) {
            const l = loadedRef.current.get(vid);
            if (!l) continue;
            try { l.el.currentTime = pos; } catch { /* not seekable yet */ }
            l.el.play().catch(() => { /* autoplay guard; will start on next user gesture */ });
          }
        }
        setReady(loadedRef.current.size > 0);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    build();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(','), config, session, versionById]);

  // Seek helper: move every graph element (so a later switch stays aligned) + the cursor.
  const seekGraph = useCallback((seconds: number) => {
    for (const l of loadedRef.current.values()) {
      try { l.el.currentTime = seconds; } catch { /* not seekable yet */ }
    }
    setPlayheadMs(Math.floor(seconds * 1000));
    // Move the cursor without triggering WaveSurfer playback.
    try { wsRef.current?.setTime(seconds); } catch { /* ignore */ }
  }, []);

  // --- Render the ACTIVE version's waveform when its identity changes (visual only). ---
  // Uses PRECOMPUTED peaks, so WaveSurfer draws immediately and performs NO audio fetch/decode.
  // Its own element is muted and never played; the audio graph is untouched, so switching a
  // version can never stop or restart playback.
  const loadedWaveformFor = useRef<string | null>(null);
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || !activeVersionId) return;
    if (loadedWaveformFor.current === activeVersionId) return; // already showing this waveform
    const l = loadedRef.current.get(activeVersionId);
    if (!l) return;
    loadedWaveformFor.current = activeVersionId;
    // Provide the proxy URL (so WaveSurfer's own muted element can scrub) AND precomputed peaks +
    // duration (so it renders instantly without decoding audio — the only way to show a waveform
    // under the MediaElement backend).
    // Load with an EMPTY url so WaveSurfer never fetches or attaches audio to its own element:
    // with precomputed peaks + duration it renders purely from the data. Passing the proxy URL
    // would make WaveSurfer setSrc() + fetch on every switch AND call pause() internally, which
    // disrupted playback. All audio comes from the gain graph; WaveSurfer is visual-only.
    const peakData = l.peaks.length ? [l.peaks] : [[0]];
    ws.load('', peakData, l.durationSecs || undefined).then(() => {
      ws.setVolume(0);
      const activeEl = loadedRef.current.get(activeVersionId)?.el;
      if (activeEl) { try { ws.setTime(activeEl.currentTime); } catch { /* ignore */ } }
    }).catch(() => { loadedWaveformFor.current = null; /* allow retry */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersionId, ready]);

  // Keep the loop mirror in sync for the rAF tick to read.
  useEffect(() => {
    loopRef.current = { enabled: loopEnabled, startMs: loopStartMs, endMs: loopEndMs };
  }, [loopEnabled, loopStartMs, loopEndMs]);

  // --- Drive the cursor + playhead readout from the ACTIVE element while playing. ---
  // Also enforces the loop: when playback passes loopEnd, seek all graph elements back to
  // loopStart (kept aligned so an A/B/C switch mid-loop stays in sync).
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const id = activeVersionId;
      const el = id ? loadedRef.current.get(id)?.el : undefined;
      if (el) {
        const secs = el.currentTime;
        const loop = loopRef.current;
        if (loop.enabled && loop.startMs != null && loop.endMs != null && loop.endMs > loop.startMs
            && secs * 1000 >= loop.endMs) {
          const backTo = loop.startMs / 1000;
          for (const l of loadedRef.current.values()) {
            try { l.el.currentTime = backTo; } catch { /* not seekable yet */ }
          }
          setPlayheadMs(loop.startMs);
          try { wsRef.current?.setTime(backTo); } catch { /* ignore */ }
        } else {
          setPlayheadMs(Math.floor(secs * 1000));
          try { wsRef.current?.setTime(secs); } catch { /* ignore */ }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    if (playing) raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [playing, activeVersionId]);

  // --- Apply gains: active slot audible, others muted (no graph rebuild). ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const t = ctx.currentTime;
    const ramp = 0.015;
    for (const [vid, l] of loadedRef.current.entries()) {
      const isActive = vid === activeVersionId;
      l.gain.gain.setTargetAtTime(isActive ? audibleGainFor(l.version) : 0, t, ramp);
    }
  }, [activeVersionId, audibleGainFor, ready]);

  // Keep the playing ref in sync so the graph-build effect can read it without depending on it.
  useEffect(() => { playingRef.current = playing; }, [playing]);

  // Notify the parent of the active version (for the approval/export panel).
  useEffect(() => {
    onApprovalContextChange?.(activeVersionId);
  }, [activeVersionId, onApprovalContextChange]);

  // Tear down all audio + close the context on unmount (leaving the track).
  useEffect(() => () => { teardownAudio(); ctxRef.current?.close(); }, [teardownAudio]);

  // --- Transport ---
  const playPause = async () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    if (playing) {
      for (const l of loadedRef.current.values()) l.el.pause();
      setPlaying(false);
      return;
    }
    // Start ALL selected sources from the same position so a switch is instant + aligned.
    const activeEl = activeVersionId ? loadedRef.current.get(activeVersionId)?.el : undefined;
    const pos = activeEl?.currentTime ?? 0;
    for (const l of loadedRef.current.values()) l.el.currentTime = pos;
    await Promise.all([...loadedRef.current.values()].map((l) => l.el.play()));
    setPlaying(true);
  };

  // Clear a drag-selected (pending) comment region, removing its waveform overlay. Shared by the
  // Esc shortcut and the composer's "use playhead instead" button.
  const clearPendingRegion = () => {
    regionsRef.current?.getRegions().forEach((r) => { if (!r.id.startsWith('comment-')) r.remove(); });
    setPendingRegion(null);
  };

  // Relative seek by `deltaSecs`, clamped to [0, duration].
  const seekBy = (deltaSecs: number) => {
    const dur = activeDurationSecs;
    if (dur <= 0) return;
    const next = Math.min(Math.max(playheadMs / 1000 + deltaSecs, 0), dur);
    seekGraph(next);
  };

  // Keyboard shortcuts (armed once audio is ready). The hook guards text inputs + focused buttons
  // (Escape excepted); see useKeyboardShortcuts.
  useKeyboardShortcuts({
    Space: () => { playPause(); },
    ArrowRight: (e) => seekBy(e.shiftKey ? 1 : 5),
    ArrowLeft: (e) => seekBy(e.shiftKey ? -1 : -5),
    Home: () => seekGraph(0),
    End: () => { if (activeDurationSecs > 0) seekGraph(activeDurationSecs); },
    KeyA: () => switchTo('A'),
    KeyB: () => switchTo('B'),
    KeyC: () => switchTo('C'),
    // 'N' focuses the comment box (jump straight to writing a note) — 'C' is taken by the A/B/C
    // version switch, so comment-focus moved to N (as in "new comment"). The hook ignores keys
    // fired from inside inputs, so this never loops.
    KeyN: () => { commentRef.current?.focus(); },
    Slash: (e) => { if (e.shiftKey) setShowHelp((v) => !v); }, // '?' = Shift+/
    Escape: () => {
      if (showHelp) { setShowHelp(false); return; }
      if (pendingRegion) clearPendingRegion();
    },
  }, ready);

  // Gapless version switch: copy the audible position to the target, flip active.
  const switchTo = (slot: Slot) => {
    const targetId = slotIds[slot];
    if (!targetId) return;
    const fromEl = activeVersionId ? loadedRef.current.get(activeVersionId)?.el : undefined;
    const toEl = loadedRef.current.get(targetId)?.el;
    if (fromEl && toEl) toEl.currentTime = fromEl.currentTime;
    setActive(slot);
  };

  // --- Loop / repeat controls (session-only) ---
  const setLoopIn = () => {
    // Mark loop start at the playhead; if it would sit at/after the end, drop the stale end.
    setLoopStartMs(playheadMs);
    if (loopEndMs != null && loopEndMs <= playheadMs) setLoopEndMs(null);
  };
  const setLoopOut = () => {
    // Mark loop end at the playhead; only valid if it's after the start.
    if (loopStartMs != null && playheadMs > loopStartMs) {
      setLoopEndMs(playheadMs);
      // Drop the playhead back to the loop start so playback runs the whole loop.
      seekGraph(loopStartMs / 1000);
    } else {
      setError('Set the loop start before a later loop end.');
    }
  };
  // Use a drag-selected region as the loop bounds, then clear the pending selection.
  const loopFromSelection = () => {
    if (!pendingRegion) return;
    setLoopStartMs(pendingRegion.startMs);
    setLoopEndMs(pendingRegion.endMs);
    setLoopEnabled(true);
    regionsRef.current?.getRegions().forEach((r) => {
      if (!r.id.startsWith('comment-') && r.id !== 'loop-region') r.remove();
    });
    setPendingRegion(null);
    // Start from the loop's beginning.
    seekGraph(pendingRegion.startMs / 1000);
  };
  const clearLoop = () => {
    setLoopEnabled(false);
    setLoopStartMs(null);
    setLoopEndMs(null);
  };
  const loopReady = loopStartMs != null && loopEndMs != null && loopEndMs > loopStartMs;

  // --- Comments for the active version ---
  const refreshComments = useMemo(
    () => async () => {
      if (!activeVersionId) { setComments([]); return; }
      try {
        const opts: { state?: 'open' | 'resolved'; author?: string } = {};
        if (filter === 'open' || filter === 'resolved') opts.state = filter;
        if (filter === 'mine' && session.email) opts.author = session.email;
        const res = await listComments(config, session, activeVersionId, opts);
        setComments(res.comments);
      } catch (e) {
        setError(`Could not load comments: ${String(e)}`);
      }
    },
    [config, session, activeVersionId, filter]
  );
  useEffect(() => { refreshComments(); }, [refreshComments]);

  // Render comment markers on the waveform for the active version. Region comments get a visible
  // left border (a "tag" edge) so a shaded span reads as a comment anchor rather than a stray
  // selection, plus a hover tooltip carrying the timecode range + comment body. Resolved comments
  // are dimmed. Point pins get the same tooltip.
  useEffect(() => {
    const regions = regionsRef.current;
    if (!regions) return;
    regions.getRegions().forEach((r) => { if (r.id.startsWith('comment-')) r.remove(); });
    for (const c of comments) {
      const isRegion = c.tEndMs != null && c.tEndMs > c.tStartMs;
      const region = regions.addRegion({
        id: `comment-${c.commentId}`,
        start: c.tStartMs / 1000,
        end: isRegion ? (c.tEndMs as number) / 1000 : undefined,
        color: isRegion ? REGION_COLOR : POINT_COLOR,
        drag: false,
        resize: false,
      });
      // Tag styling + tooltip on the rendered element (same hook the loop span uses).
      const el = (region as unknown as { element?: HTMLElement }).element;
      if (el) {
        const range = isRegion ? `${fmt(c.tStartMs)}–${fmt(c.tEndMs as number)}` : fmt(c.tStartMs);
        const preview = c.body.length > 80 ? `${c.body.slice(0, 77)}…` : c.body;
        el.title = `${range} · ${c.authorEmail || c.author}${c.resolved ? ' (resolved)' : ''}\n${preview}`;
        el.style.cursor = 'pointer';
        if (isRegion) {
          // A tag edge on the region's start so its anchor point is unmistakable.
          el.style.borderLeft = `2px solid ${REGION_EDGE}`;
          el.style.boxSizing = 'border-box';
        }
        if (c.resolved) el.style.opacity = '0.4';
      }
    }
  }, [comments]);

  // Render the loop span on the waveform (distinct colour). Cleared when no complete loop is set.
  useEffect(() => {
    const regions = regionsRef.current;
    if (!regions) return;
    regions.getRegions().forEach((r) => { if (r.id === 'loop-region') r.remove(); });
    if (loopStartMs != null && loopEndMs != null && loopEndMs > loopStartMs) {
      const region = regions.addRegion({
        id: 'loop-region',
        start: loopStartMs / 1000,
        end: loopEndMs / 1000,
        color: LOOP_COLOR,
        drag: false,
        resize: false,
      });
      // Set the loop span apart with bright borders on both edges.
      const el = (region as unknown as { element?: HTMLElement }).element;
      if (el) {
        el.style.borderLeft = '2px solid #8575ff';
        el.style.borderRight = '2px solid #8575ff';
        el.style.boxSizing = 'border-box';
      }
    }
  }, [loopStartMs, loopEndMs, ready]);

  const seekTo = (ms: number) => { seekGraph(ms / 1000); };

  // Active version's duration (secs) for the waveform slider bounds + keyboard seek clamp.
  const activeDurationSecs = (activeVersionId && loadedRef.current.get(activeVersionId)?.durationSecs) || 0;

  // Keyboard seek on the waveform (a11y): the WaveSurfer canvas is mouse-only, so a focusable
  // slider wrapper handles arrows/Home/End. Left/Right ±5s, Up/Down ±1s, PageUp/Down ±10s.
  const onWaveKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (activeDurationSecs <= 0) return;
    const cur = playheadMs / 1000;
    let next: number | null = null;
    switch (e.key) {
      case 'ArrowRight': next = cur + 5; break;
      case 'ArrowLeft': next = cur - 5; break;
      case 'ArrowUp': next = cur + 1; break;
      case 'ArrowDown': next = cur - 1; break;
      case 'PageUp': next = cur + 10; break;
      case 'PageDown': next = cur - 10; break;
      case 'Home': next = 0; break;
      case 'End': next = activeDurationSecs; break;
      default: return;
    }
    e.preventDefault();
    seekGraph(Math.min(Math.max(next, 0), activeDurationSecs));
  };

  const submitComment = async () => {
    const body = draft.trim();
    if (!body || !activeVersionId) return;
    setBusy(true);
    setError(null);
    try {
      const pinnedVersionId = pinToVersion ? activeVersionId : null;
      const input = pendingRegion
        ? { body, tStartMs: pendingRegion.startMs, tEndMs: pendingRegion.endMs, pinnedVersionId }
        : { body, tStartMs: playheadMs, pinnedVersionId };
      const created = await createComment(config, session, activeVersionId, input);
      setComments((prev) =>
        [...prev, created].sort((a, b) => a.tStartMs - b.tStartMs || a.createdAt.localeCompare(b.createdAt))
      );
      setDraft('');
      if (pendingRegion) {
        regionsRef.current?.getRegions().forEach((r) => { if (!r.id.startsWith('comment-')) r.remove(); });
        setPendingRegion(null);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleThread = async (commentId: string) => {
    const willExpand = !expanded[commentId];
    setExpanded((p) => ({ ...p, [commentId]: willExpand }));
    if (willExpand && threads[commentId] === undefined && activeVersionId) {
      try {
        const res = await listReplies(config, session, activeVersionId, commentId);
        setThreads((p) => ({ ...p, [commentId]: res.replies }));
      } catch (e) { setError(`Could not load replies: ${String(e)}`); }
    }
  };

  const submitReply = async (commentId: string) => {
    const body = (replyDraft[commentId] || '').trim();
    if (!body || !activeVersionId) return;
    try {
      const reply = await addReply(config, session, activeVersionId, commentId, body);
      setThreads((p) => ({ ...p, [commentId]: [...(p[commentId] || []), reply] }));
      setReplyDraft((p) => ({ ...p, [commentId]: '' }));
      setComments((prev) => prev.map((c) => (c.commentId === commentId ? { ...c, replyCount: c.replyCount + 1 } : c)));
    } catch (e) { setError(String(e)); }
  };

  const toggleResolved = async (c: Comment) => {
    if (!activeVersionId) return;
    try {
      const updated = await setCommentResolved(config, session, activeVersionId, c.commentId, !c.resolved);
      if (filter === 'open' || filter === 'resolved') await refreshComments();
      else setComments((prev) => prev.map((x) => (x.commentId === c.commentId ? updated : x)));
    } catch (e) { setError(String(e)); }
  };

  const restoreActive = async () => {
    if (!activeVersionId) return;
    setError(null);
    try {
      await restoreVersion(config, session, activeVersionId, {});
      const res = await listTrackVersions(config, session, projectId, trackId);
      const r = res.versions.filter((v) => v.primaryAsset?.ingestStatus === 'ready');
      setVersions(r);
      setSlotIds((prev) => ({ ...prev, A: r[0]?.versionId ?? prev.A }));
      setActive('A');
    } catch (e) { setError(`Restore failed: ${String(e)}`); }
  };

  const activeVersion = activeVersionId ? versionById.get(activeVersionId) : undefined;

  const slotLabel = (slot: Slot): string => {
    const id = slotIds[slot];
    if (!id) return `${slot}: —`;
    const v = versionById.get(id);
    return `${slot}: ${v?.ordinal != null ? `v${v.ordinal}` : id.slice(0, 8)}`;
  };

  const noVersions = versions.length === 0;

  return (
    <div>
      {noVersions && (
        <p style={{ color: 'var(--text-muted)' }}>No ready versions yet. Upload and wait for ingest to finish.</p>
      )}
      {/* Waveform transport. The container is ALWAYS mounted (even before versions load) so the
          one-time WaveSurfer-create effect finds the ref — otherwise it bails on mount and never
          re-runs. Hidden until there's a version to show. */}
      <div
        ref={waveRef}
        onKeyDown={onWaveKeyDown}
        role="slider"
        tabIndex={ready ? 0 : -1}
        aria-label="Waveform seek position"
        aria-valuemin={0}
        aria-valuemax={Math.floor(activeDurationSecs)}
        aria-valuenow={Math.floor(playheadMs / 1000)}
        aria-valuetext={`${fmt(playheadMs)}${activeDurationSecs ? ` of ${fmt(activeDurationSecs * 1000)}` : ''}`}
        style={{ marginTop: 8, display: noVersions ? 'none' : 'block' }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <Btn
          variant="primary"
          onClick={playPause}
          disabled={!ready}
          icon={playing ? <Icon.pause /> : <Icon.play />}
          iconOnly
          aria-label={playing ? 'Pause' : 'Play'}
          title={playing ? 'Pause' : 'Play'}
        />
        <span style={{ fontFamily: t.mono, fontVariantNumeric: 'tabular-nums', color: t.muted }}>{fmt(playheadMs)}</span>

        {/* A/B/C switch — flips what you HEAR; the playhead keeps moving. */}
        <div role="group" aria-label="Audible version" style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
          {SLOTS.map((slot) => (
            <Btn
              key={slot}
              variant={active === slot ? 'secondary' : 'ghost'}
              onClick={() => switchTo(slot)}
              disabled={!ready || !slotIds[slot]}
              aria-pressed={active === slot}
              aria-label={slotIds[slot] ? `Hear ${slotLabel(slot)}` : `Version ${slot} empty`}
              title={slotIds[slot] ? `Hear ${slotLabel(slot)}` : `${slot}: empty`}
              style={{ minWidth: 34, ...(active === slot ? { borderColor: t.accent, color: t.accent } : {}) }}
            >
              {slot}
            </Btn>
          ))}
        </div>

        <label style={{ marginLeft: 8, fontSize: 13, color: t.muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={loudnessMatch} onChange={(e) => setLoudnessMatch(e.target.checked)} />
          Loudness match ({loudnessRef} LUFS)
        </label>
        {loading && <span style={{ color: t.muted, fontSize: 13 }}>loading…</span>}
      </div>

      {/* Loop / repeat — set in/out at the playhead (or from a drag-selection), toggle repeat.
          Session-only. While looping you can flip A/B/C to compare the same passage. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <Btn
          variant={loopEnabled ? 'secondary' : 'ghost'}
          onClick={() => setLoopEnabled((v) => !v)}
          disabled={!loopReady}
          aria-pressed={loopEnabled}
          icon={<Icon.loop />}
          title={loopReady ? 'Toggle repeat over the loop region' : 'Set loop in and out first'}
          style={loopEnabled ? { borderColor: t.accent, color: t.accent } : undefined}
        >
          {loopEnabled ? 'Loop on' : 'Loop off'}
        </Btn>
        <Btn variant="ghost" onClick={setLoopIn} disabled={!ready}>Set loop in</Btn>
        <Btn variant="ghost" onClick={setLoopOut} disabled={!ready || loopStartMs == null}>Set loop out</Btn>
        {pendingRegion && (
          <Btn variant="ghost" onClick={loopFromSelection} title="Use the highlighted selection as the loop">
            Mark loop from selection
          </Btn>
        )}
        <span style={{ fontSize: 13, color: t.muted }}>
          {loopReady
            ? `Loop ${fmt(loopStartMs as number)} – ${fmt(loopEndMs as number)}`
            : loopStartMs != null
              ? `Loop in ${fmt(loopStartMs)} — set loop out`
              : 'No loop set'}
        </span>
        {(loopStartMs != null || loopEndMs != null) && (
          <Btn variant="danger" onClick={clearLoop} icon={<Icon.x />} iconOnly aria-label="Clear loop" title="Clear loop" />
        )}
      </div>

      {/* Slot pickers: choose which version each of A/B/C points to. */}
      <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
        {SLOTS.map((slot) => (
          <label key={slot} style={{ fontSize: 13 }}>
            {slot}
            <br />
            <select
              value={slotIds[slot] ?? ''}
              onChange={(e) => setSlotIds((prev) => ({ ...prev, [slot]: e.target.value || null }))}
            >
              <option value="">— none —</option>
              {versions.map((v) => (
                <option key={v.versionId} value={v.versionId}>
                  {v.ordinal != null ? `v${v.ordinal}` : v.versionId.slice(0, 8)}
                  {v.label ? ` — ${v.label}` : ''}
                </option>
              ))}
            </select>
          </label>
        ))}
        <Btn variant="ghost" onClick={restoreActive} disabled={!ready} title="Duplicate the audible version as a new latest version" style={{ alignSelf: 'flex-end' }}>
          Restore active as latest
        </Btn>
      </div>

      {activeVersion?.primaryAsset && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
          Now hearing <strong>{active}</strong>
          {activeVersion.ordinal != null ? ` (v${activeVersion.ordinal})` : ''} ·{' '}
          {activeVersion.primaryAsset.integratedLufs ?? '—'} LUFS ·{' '}
          {activeVersion.primaryAsset.truePeakDbtp ?? '—'} dBTP
          {loudnessMatch && (
            <>
              {' '}· applied gain{' '}
              {matchGainDb(activeVersion.primaryAsset.integratedLufs, activeVersion.primaryAsset.truePeakDbtp, loudnessRef).toFixed(1)} dB
            </>
          )}
        </p>
      )}

      {/* Comment composer — targets the ACTIVE version. */}
      {canComment && activeVersionId && (
        <div style={{ marginTop: 16, padding: 12, border: `1px solid ${t.border}`, borderRadius: t.radiusSm }}>
          <strong>
            {pendingRegion
              ? `Comment on region ${fmt(pendingRegion.startMs)}–${fmt(pendingRegion.endMs)}`
              : `Comment at ${fmt(playheadMs)}`}
            {activeVersion?.ordinal != null ? ` · on v${activeVersion.ordinal}` : ''}
          </strong>
          {pendingRegion && (
            <Btn
              variant="ghost"
              onClick={clearPendingRegion}
              style={{ marginLeft: 8 }}
            >
              use playhead instead
            </Btn>
          )}
          <br />
          <textarea
            ref={commentRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            style={{ width: '100%', marginTop: 8 }}
            aria-label="Comment text"
            placeholder="Tip: drag across the waveform to comment on a region, or type to comment at the playhead."
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
            <Btn variant="primary" onClick={submitComment} disabled={busy || !draft.trim()}>
              {busy ? 'Posting…' : 'Post comment'}
            </Btn>
            <label style={{ fontSize: 13, color: t.muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={pinToVersion} onChange={(e) => setPinToVersion(e.target.checked)} />
              pin to this version only
            </label>
          </div>
        </div>
      )}

      {/* Comment list for the active version */}
      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Comments ({comments.length})</h3>
        <span role="group" aria-label="Filter comments" style={{ display: 'flex', gap: 4 }}>
          {(['all', 'open', 'resolved', 'mine'] as Filter[]).map((f) => (
            <Btn
              key={f}
              variant={filter === f ? 'secondary' : 'ghost'}
              onClick={() => setFilter(f)}
              disabled={f === 'mine' && !session.email}
              aria-pressed={filter === f}
              style={{ textTransform: 'capitalize', ...(filter === f ? { borderColor: t.accent, color: t.accent } : {}) }}
            >
              {f}
            </Btn>
          ))}
        </span>
      </div>
      {comments.length === 0 && <p style={{ color: t.muted }}>No comments{filter !== 'all' ? ` (${filter})` : ''} yet.</p>}
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {comments.map((c) => {
          const isRegion = c.tEndMs != null && c.tEndMs > c.tStartMs;
          const isExpanded = !!expanded[c.commentId];
          const replies = threads[c.commentId];
          return (
            <li key={c.commentId} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', opacity: c.resolved ? 0.6 : 1 }}>
              <button
                onClick={() => seekTo(c.tStartMs)}
                title="Seek to this timecode"
                style={{ fontFamily: 'monospace', cursor: 'pointer', border: 'none', background: 'none', color: 'var(--accent)', padding: 0 }}
              >
                {isRegion ? `${fmt(c.tStartMs)}–${fmt(c.tEndMs as number)}` : fmt(c.tStartMs)}
              </button>
              {' — '}
              <span>{renderBody(c.body)}</span>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{c.authorEmail || c.author} · {new Date(c.createdAt).toLocaleString()}</span>
                {c.resolved && <span style={{ color: t.accent }}>✓ resolved</span>}
                <Btn variant="ghost" onClick={() => toggleThread(c.commentId)}>
                  {isExpanded ? 'Hide' : 'Replies'}{c.replyCount ? ` (${c.replyCount})` : ''}
                </Btn>
                {canComment && (
                  <Btn variant="ghost" onClick={() => toggleResolved(c)} icon={c.resolved ? undefined : <Icon.check />}>
                    {c.resolved ? 'Reopen' : 'Resolve'}
                  </Btn>
                )}
              </div>

              {isExpanded && (
                <div style={{ margin: '8px 0 0 16px', paddingLeft: 12, borderLeft: '2px solid var(--border)' }}>
                  {replies === undefined && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading replies…</p>}
                  {replies && replies.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No replies.</p>}
                  {replies && replies.map((r) => (
                    <div key={r.replyId} style={{ padding: '4px 0' }}>
                      <span style={{ fontSize: 14 }}>{renderBody(r.body)}</span>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {r.authorEmail || r.author} · {new Date(r.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                  {canComment && (
                    <div style={{ marginTop: 4 }}>
                      <input
                        value={replyDraft[c.commentId] || ''}
                        onChange={(e) => setReplyDraft((p) => ({ ...p, [c.commentId]: e.target.value }))}
                        placeholder="Reply… (@mention supported)"
                        aria-label="Reply text"
                        style={{ width: '70%' }}
                      />
                      <Btn variant="secondary" onClick={() => submitReply(c.commentId)} disabled={!(replyDraft[c.commentId] || '').trim()} style={{ marginLeft: 6 }}>
                        Reply
                      </Btn>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      <ShortcutsHelp open={showHelp} onClose={() => setShowHelp(false)} rows={TRACK_SHORTCUTS} />
    </div>
  );
}

// Shown in the '?' help overlay for the mix player.
const TRACK_SHORTCUTS: ShortcutRow[] = [
  { keys: ['Space'], label: 'Play / pause' },
  { keys: ['←'], label: 'Back 5 seconds' },
  { keys: ['→'], label: 'Forward 5 seconds' },
  { keys: ['Shift', '←'], label: 'Back 1 second' },
  { keys: ['Shift', '→'], label: 'Forward 1 second' },
  { keys: ['Home'], label: 'Jump to start' },
  { keys: ['End'], label: 'Jump to end' },
  { keys: ['A'], label: 'Hear version A' },
  { keys: ['B'], label: 'Hear version B' },
  { keys: ['C'], label: 'Hear version C' },
  { keys: ['N'], label: 'Write a comment' },
  { keys: ['?'], label: 'Show this help' },
  { keys: ['Esc'], label: 'Close help / clear selection' },
];
